import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, ArrowDownUp, Eye, Pencil } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/Select'
import { DataTable, TableColumn } from '@/components/ui/DataTable'
import { Paginator } from '@/components/ui/Paginator'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAuth } from '@/contexts/AuthContext'
import { useUserList } from '@/hooks/useUserList'
import { UserModel, UserRole } from '@/models/user.model'
import { cn } from '@/utils/cn'
import { paths } from '@/routes/path'

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const COMPLETION_FILTERS = [
  { value: 'all', label: 'All cohorts' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not-started', label: 'Not started' },
]

function formatDateTime(timestamp: string | null): string {
  if (!timestamp) return '—'
  try {
    const date = new Date(timestamp)
    return `${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} | ${date.toLocaleDateString()}`
  } catch {
    return '—'
  }
}

function getCompletionState(student: UserModel): 'completed' | 'in-progress' | 'not-started' {
  if (student.programmeCohortCompletedAt) return 'completed'
  if (student.programmeCohortAssignedAt) return 'in-progress'
  return 'not-started'
}

function completionBadgeClasses(state: 'completed' | 'in-progress' | 'not-started') {
  switch (state) {
    case 'completed':
      return 'bg-blue-50 text-blue-700 border border-blue-100'
    case 'in-progress':
      return 'bg-amber-50 text-amber-700 border border-amber-100'
    default:
      return 'bg-secondary-100 text-secondary-600 border border-secondary-200'
  }
}

export function StudentManagementPage() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === UserRole.ADMIN
  const isInstructor = user?.role === UserRole.INSTRUCTOR
  const canAccess = Boolean(user && (isAdmin || isInstructor))
  const canEditLearners = Boolean(isAdmin)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [completionFilter, setCompletionFilter] = useState<string>('all')

  const { data, isFetching, isPlaceholderData } = useUserList({
    page,
    perPage,
    search: searchTerm || undefined,
    role: UserRole.LEARNER,
    isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
  })

  // Filter pre-fetched learner list locally for completion states so we avoid redundant API calls.
  const learners = useMemo(() => {
    const raw = data?.data ?? []
    if (completionFilter === 'all') {
      return raw
    }

    return raw.filter((student) => getCompletionState(student) === completionFilter)
  }, [data?.data, completionFilter])

  const meta = data?.meta

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!canAccess) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-secondary-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-secondary-900">Access restricted</CardTitle>
            <CardDescription className="text-secondary-600">
              Student management is available to authorized staff members only.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const columns: TableColumn<UserModel>[] = useMemo(() => {
    const badgeForStatus = (isActive: boolean | null) =>
      cn(
        'rounded-full px-3 py-1 text-xs font-semibold',
        isActive
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-secondary-100 text-secondary-600 border border-secondary-200',
      )

    return [
      {
        key: 'serial',
        header: 'S/N',
        className: 'w-16',
        align: 'center',
        render: (_row, index) => ((page - 1) * perPage) + index + 1,
      },
      {
        key: 'name',
        header: 'Student Name',
        render: (student) => (
          <div className="flex items-center gap-3">
            {student.avatar ? (
              <img
                src={student.avatar}
                alt={student.fullName}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-secondary-100"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-sm font-semibold text-secondary-600">
                {student.initials || '?'}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold text-secondary-900">{student.fullName || 'Unnamed learner'}</p>
              <p className="truncate text-sm text-secondary-500">{student.email ?? '—'}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'faculty',
        header: 'Faculty',
        render: (student) => <span className="text-sm text-secondary-700">{student.facultyName || '—'}</span>,
      },
      {
        key: 'department',
        header: 'Department',
        render: (student) => <span className="text-sm text-secondary-700">{student.departmentName || '—'}</span>,
      },
      {
        key: 'level',
        header: 'Level',
        align: 'center',
        render: (student) => (
          <span className="text-sm text-secondary-700">{student.level ?? student.year ?? '—'}</span>
        ),
      },
      {
        key: 'date',
        header: 'Date & Time',
        render: (student) => (
          <span className="text-sm text-secondary-500">
            {formatDateTime(student.programmeCohortAssignedAt ?? student.createdAt)}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (student) => (
          <Badge className={badgeForStatus(student.isActive)}>
            {student.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        key: 'completion',
        header: 'Completion',
        render: (student) => {
          const state = getCompletionState(student)
          const label =
            state === 'completed' ? 'Completed' : state === 'in-progress' ? 'In progress' : 'Not Started'

          return (
            <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold', completionBadgeClasses(state))}>
              {label}
            </Badge>
          )
        },
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        className: 'w-40',
        render: (student) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-secondary-500 hover:text-secondary-800"
              onClick={() => student.id && navigate(paths.app.users.detail.build(student.id))}
              disabled={!student.id}
            >
              <Eye className="mr-1.5 h-4 w-4" />
              View
            </Button>
            {canEditLearners && (
              <Button
                variant="ghost"
                size="sm"
                className="text-secondary-500 hover:text-secondary-800"
                onClick={() => student.id && navigate(paths.app.studentManagement.edit.build(student.id))}
                disabled={!student.id}
              >
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        ),
      },
    ]
  }, [canEditLearners, navigate, page, perPage])

  return (
    <div className="space-y-6">
      <Card className="border-none bg-transparent shadow-none">
        <CardHeader className="px-0 pt-0">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold text-secondary-900">Student Management</CardTitle>
              <CardDescription className="mt-1 text-secondary-600">
                Track learner progress and maintain accurate cohort information.
              </CardDescription>
              {!canEditLearners && (
                <p className="mt-2 text-sm text-secondary-500">
                  Editing learner information is restricted to administrator accounts.
                </p>
              )}
            </div>
            <Select
              value={completionFilter}
              onChange={(event) => {
                setCompletionFilter(event.target.value)
                setPage(1)
              }}
              className="w-48"
            >
              {COMPLETION_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          <div className="flex flex-col gap-4 rounded-2xl border border-secondary-100 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
                <Input
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value)
                    setPage(1)
                  }}
                  placeholder="Search learners by name or email"
                  className="pl-10"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value)
                  setPage(1)
                }}
                className="w-full sm:w-48"
              >
                {STATUS_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Select
                value={String(perPage)}
                onChange={(event) => {
                  setPerPage(Number(event.target.value))
                  setPage(1)
                }}
                className="w-full sm:w-36"
              >
                {[10, 20, 50].map((option) => (
                  <option key={option} value={option}>
                    {option} per page
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-secondary-600 hover:text-secondary-900">
                <ArrowDownUp className="mr-2 h-4 w-4" />
                Sort
              </Button>
              <Button variant="ghost" size="sm" className="text-secondary-600 hover:text-secondary-900">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <DataTable<UserModel>
        data={learners}
        columns={columns}
        isLoading={isFetching && !data}
        emptyState={
          <div className="py-10 text-center">
            <p className="text-lg font-semibold text-secondary-900">No learners found</p>
            <p className="mt-1 text-sm text-secondary-500">
              Adjust filters or try searching with a different keyword.
            </p>
          </div>
        }
        getRowId={(student) => student.id ?? `${student.email}-${student.createdAt}`}
      />

      <Paginator
        meta={meta}
        page={page}
        perPage={perPage}
        onPageChange={(nextPage) => setPage(Math.max(1, nextPage))}
        onPerPageChange={(value) => {
          setPerPage(value)
          setPage(1)
        }}
        isPreviousData={isPlaceholderData || isFetching}
      />
    </div>
  )
}
